/**
 * Cookie Categories Configuration for CookieConsent
 * Based on Rulevia Cookie Policy (v1.1)
 * Maps cookie categories to services and consent states
 */

export interface CookieCategory {
  name: string;
  enabled: boolean;
  readOnly: boolean;
  title: string;
  description: string;
  linkedCategory?: string;
}

export interface CookieService {
  name: string;
  category: string;
  cookies: string[];
  description: string;
  privacy_policy?: string;
}

/**
 * Cookie categories based on GDPR compliance
 * Maps to Google Consent Mode v2 parameters
 */
export const COOKIE_CATEGORIES: Record<string, CookieCategory> = {
  necessary: {
    name: 'necessary',
    enabled: true,
    readOnly: true,
    title: 'Essential',
    description: 'These cookies are strictly necessary for our website to function properly and cannot be disabled without impacting core functionality. They include authentication, security, and basic website operation cookies.'
  },
  
  functional: {
    name: 'functional',
    enabled: true,
    readOnly: false,
    title: 'Functional',
    description: 'These cookies enhance your experience by remembering your preferences and settings. They help us provide personalized features and improve usability.'
  },
  
  analytics: {
    name: 'analytics',
    enabled: false,
    readOnly: false,
    title: 'Analytics',
    description: 'These cookies help us understand how visitors use our website and platform. They allow us to measure performance, identify issues, and improve user experience.'
  },
  
  marketing: {
    name: 'marketing',
    enabled: false,
    readOnly: false,
    title: 'Marketing',
    description: 'These cookies are used for marketing analytics, campaign attribution, and improving our advertising effectiveness. They help us show you more relevant content.'
  }
};

/**
 * Services and their associated cookies
 * Based on Rulevia Cookie Policy and current integrations
 */
export const COOKIE_SERVICES: CookieService[] = [
  // NECESSARY COOKIES
  {
    name: 'Supabase Authentication',
    category: 'necessary',
    cookies: ['sb-*', '_supabase_auth_token', '_session_id'],
    description: 'User authentication and session management'
  },
  {
    name: 'Security & CSRF Protection',
    category: 'necessary',
    cookies: ['_csrf_token', '_api_auth'],
    description: 'Cross-site request forgery protection and API authentication'
  },
  {
    name: 'Load Balancing',
    category: 'necessary',
    cookies: ['_load_balancer'],
    description: 'Server load balancing and routing via Vercel'
  },
  {
    name: 'AI Processing',
    category: 'necessary',
    cookies: ['_ai_session', '_ai_session_id', '_generation_queue'],
    description: 'AI request session tracking and queue management'
  },
  
  // FUNCTIONAL COOKIES
  {
    name: 'User Preferences',
    category: 'functional',
    cookies: ['_user_preferences', '_language', '_timezone', '_theme'],
    description: 'Dashboard settings, language, timezone, and UI preferences'
  },
  {
    name: 'AI Model Preferences',
    category: 'functional',
    cookies: ['_ai_model_pref', '_generation_settings', '_analysis_cache', '_model_routing'],
    description: 'AI model selection and generation parameter preferences'
  },
  
  // ANALYTICS COOKIES
  {
    name: 'Google Analytics',
    category: 'analytics',
    cookies: ['_ga', '_ga_*', '_gid', '_gat_gtag_*'],
    description: 'Website performance analysis and user behavior insights',
    privacy_policy: 'https://policies.google.com/privacy'
  },
  {
    name: 'Microsoft Clarity',
    category: 'analytics',
    cookies: ['_clck', '_clsk', 'CLID'],
    description: 'User behavior analytics and session recordings',
    privacy_policy: 'https://privacy.microsoft.com/privacystatement'
  },
  {
    name: 'Sentry Monitoring',
    category: 'analytics',
    cookies: ['sentry-*'],
    description: 'Error monitoring and performance tracking',
    privacy_policy: 'https://sentry.io/privacy/'
  },
  
  // MARKETING COOKIES
  {
    name: 'Meta (Facebook) Pixel',
    category: 'marketing',
    cookies: ['_fbp', 'fr'],
    description: 'Marketing campaign performance and retargeting',
    privacy_policy: 'https://www.facebook.com/privacy/policy/'
  },
  {
    name: 'Reddit Pixel',
    category: 'marketing',
    cookies: ['_rdt_uuid'],
    description: 'Advertising attribution and audience insights',
    privacy_policy: 'https://www.reddit.com/policies/privacy-policy'
  },
  {
    name: 'Google Tag Manager',
    category: 'marketing',
    cookies: ['_gtm_*'],
    description: 'Campaign tracking and marketing analytics',
    privacy_policy: 'https://policies.google.com/privacy'
  },
  
  // SECURITY COOKIES (Treated as necessary)
  {
    name: 'Google reCAPTCHA',
    category: 'necessary',
    cookies: ['_GRECAPTCHA', 'rc::a', 'rc::b', 'rc::c'],
    description: 'Spam prevention and security protection',
    privacy_policy: 'https://policies.google.com/privacy'
  }
];

/**
 * Maps cookie categories to Google Consent Mode v2 parameters
 */
export const CONSENT_MODE_MAPPING = {
  necessary: {
    functionality_storage: 'granted',
    security_storage: 'granted',
    personalization_storage: 'granted'
  },
  functional: {
    functionality_storage: 'granted',
    personalization_storage: 'granted'
  },
  analytics: {
    analytics_storage: 'granted'
  },
  marketing: {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted'
  }
} as const;

/**
 * Get consent mode updates based on user choices
 */
export function getConsentModeUpdate(categories: Record<string, boolean>) {
  const consentUpdate: Record<string, 'granted' | 'denied'> = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    personalization_storage: 'granted',
    security_storage: 'granted'
  };

  // Update based on user choices
  if (categories.analytics) {
    consentUpdate.analytics_storage = 'granted';
  }
  
  if (categories.marketing) {
    consentUpdate.ad_storage = 'granted';
    consentUpdate.ad_user_data = 'granted';
    consentUpdate.ad_personalization = 'granted';
  }

  return consentUpdate;
}